import React, { useState, useEffect } from 'react';
import { Mic, Play, Users, Lock, ShoppingCart, ArrowUp, Video, FileText, Search, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Mindset = () => {
  const [user, setUser] = useState(null);
  const [contents, setContents] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      if (token) {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { headers });
        setUser(userRes.data);
      }

      const mindsetRes = await axios.get(`${BACKEND_URL}/api/content?category=mindset`, { headers });
      setContents(mindsetRes.data || []);
      setPodcasts([]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const canAccessContent = (content) => {
    const contentLevels = content.levels || [content.level];
    
    if (!user) return contentLevels.includes('free');
    
    const accessLevels = {
      'free': ['free'],
      'principiante': ['free', 'nivel_1'],
      'intermedio': ['free', 'nivel_1', 'nivel_2'],
      'avanzado': ['free', 'nivel_1', 'nivel_2', 'nivel_3'],
      'premium': ['free', 'nivel_1', 'nivel_2', 'nivel_3', 'premium']
    };

    const userAllowedLevels = accessLevels[user.subscription_level] || ['free'];
    return contentLevels.some(lvl => userAllowedLevels.includes(lvl));
  };

  const handleBuyContent = async (content) => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      toast.error('Debes iniciar sesión para comprar');
      window.location.href = '/login';
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/create-checkout-session`,
        {
          content_id: content.content_id,
          price: content.price,
          title: content.title,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Error al procesar la compra');
    }
  };

  const levelNames = {
    'free': 'Gratis',
    'nivel_1': 'Nivel 1',
    'nivel_2': 'Nivel 2',
    'nivel_3': 'Nivel 3',
    'premium': 'Premium'
  };

  const allContent = [
    ...contents,
    ...podcasts.map(p => ({
      content_id: p.podcast_id,
      title: p.title,
      description: p.guest ? `Con ${p.guest}` : '',
      content_type: 'video',
      level: 'free',
      levels: ['free'],
      thumbnail_url: p.image_url,
      duration: p.duration,
      file_path: p.audio_url || p.video_url,
      isPodcast: true
    }))
  ];

  const filteredContent = allContent.filter(content =>
    content.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400 text-sm font-medium uppercase tracking-wider">Desarrollo Personal</span>
          </div>
          
          <h1 className="font-barlow text-4xl md:text-6xl font-black uppercase text-white mb-4" data-testid="mindset-title">
            MINDSET
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            Conversatorios, podcasts y contenido de motivación y desarrollo personal
          </p>
          
          {user && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full">
              <span className="text-purple-400 text-sm font-medium">
                Tu nivel: {levelNames[user.subscription_level] || 'Gratuito'}
              </span>
            </div>
          )}
        </div>

        {/* Mensaje para no logueados */}
        {!user && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-8 mb-12 text-center">
            <p className="text-lg text-zinc-300 mb-4">Algunos contenidos están disponibles solo para miembros</p>
            <a href="/programs">
              <Button className="bg-purple-600 hover:bg-purple-500 text-white rounded-full px-8 py-3 font-bold">
                Ver Programas
              </Button>
            </a>
          </div>
        )}

        {/* Buscador */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar contenido..."
              className="pl-12 rounded-full py-6 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-purple-500"
            />
          </div>
        </div>

        {/* Contenido */}
        {filteredContent.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-zinc-800 rounded-3xl">
            <Mic className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No hay contenido disponible</h3>
            <p className="text-zinc-500">Pronto agregaremos nuevo contenido de mindset</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {filteredContent.map((content, index) => {
              const hasAccess = canAccessContent(content);
              const canBuyIndividually = content.price && content.price > 0;
              const contentLevels = content.levels || [content.level];

              return (
                <div 
                  key={content.content_id || index} 
                  className="group bg-zinc-900/50 border border-zinc-800 rounded-3xl overflow-hidden hover:border-purple-500/50 transition-all"
                  data-testid={`mindset-content-${index + 1}`}
                >
                  {/* Preview */}
                  <div className="relative">
                    <div className="aspect-video bg-zinc-800 flex items-center justify-center overflow-hidden">
                      {content.thumbnail_url || content.cover_image ? (
                        <img 
                          src={(content.thumbnail_url || content.cover_image).startsWith('http') 
                            ? (content.thumbnail_url || content.cover_image)
                            : `${BACKEND_URL}${content.thumbnail_url || content.cover_image}`
                          }
                          alt={content.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : content.content_type === 'video' ? (
                        <Video className="w-16 h-16 text-zinc-600" />
                      ) : (
                        <FileText className="w-16 h-16 text-zinc-600" />
                      )}
                    </div>

                    {/* Overlay de bloqueo */}
                    {!hasAccess && (
                      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center">
                        <Lock className="w-10 h-10 text-white mb-2" />
                        <span className="text-sm font-medium text-white">Contenido Bloqueado</span>
                      </div>
                    )}

                    {/* Play button para contenido accesible */}
                    {hasAccess && content.content_type === 'video' && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>
                    )}

                    {/* Badge de tipo */}
                    <span className={`absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full ${
                      content.content_type === 'video' ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                    }`}>
                      {content.content_type === 'video' ? 'Video' : 'PDF'}
                    </span>

                    {/* Badge de nivel */}
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1">
                      {contentLevels.slice(0, 2).map((lvl, idx) => (
                        <span key={idx} className="text-xs font-bold px-2 py-1 rounded-full bg-zinc-900/80 text-zinc-300 border border-zinc-700">
                          {levelNames[lvl] || lvl}
                        </span>
                      ))}
                    </div>

                    {/* Badge de precio */}
                    {!hasAccess && canBuyIndividually && (
                      <span className="absolute bottom-3 right-3 text-xs font-bold px-3 py-1 rounded-full bg-green-500 text-white">
                        ${content.price}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-widest text-purple-400">
                        {content.guest ? 'Podcast' : 'Mindset'}
                      </span>
                      {content.duration && (
                        <span className="text-sm text-zinc-500">{content.duration}</span>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-barlow font-bold text-white mb-2 line-clamp-2">
                      {content.title}
                    </h3>
                    
                    {content.guest && (
                      <div className="flex items-center text-zinc-400 mb-3">
                        <Mic className="w-4 h-4 mr-2 flex-shrink-0 text-purple-400" />
                        <span className="text-sm">Con: {content.guest}</span>
                      </div>
                    )}
                    
                    {content.description && (
                      <p className="text-zinc-500 text-sm mb-4 line-clamp-2">
                        {content.description}
                      </p>
                    )}

                    {/* Botones de acción */}
                    {hasAccess ? (
                      (() => {
                        // Determinar la URL del contenido correctamente
                        let contentUrl = '#';
                        const videoPath = content.file_path || content.video_url || content.external_url;
                        
                        if (videoPath) {
                          // Si es una URL externa (YouTube, Drive, etc.)
                          if (videoPath.startsWith('http://') || videoPath.startsWith('https://')) {
                            contentUrl = videoPath;
                          } else {
                            // Si es un archivo local del servidor
                            contentUrl = `${BACKEND_URL}${videoPath}`;
                          }
                        }
                        
                        return (
                          <a 
                            href={contentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full bg-purple-600 hover:bg-purple-500 text-white rounded-full py-3 font-bold transition-colors"
                          >
                            <Play className="w-4 h-4" />
                            {content.content_type === 'video' ? 'Ver Ahora' : 'Descargar'}
                          </a>
                        );
                      })()
                    ) : (
                      <div className="space-y-2">
                        {canBuyIndividually && (
                          <Button
                            onClick={() => handleBuyContent(content)}
                            className="w-full bg-green-600 hover:bg-green-500 text-white rounded-full py-3 font-bold"
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Comprar por ${content.price}
                          </Button>
                        )}
                        
                        <a href="/programs" className="block">
                          <Button
                            className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 rounded-full py-3 font-bold"
                          >
                            <ArrowUp className="w-4 h-4 mr-2" />
                            {canBuyIndividually ? 'O Subir de Nivel' : 'Subir de Nivel'}
                          </Button>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA Final */}
        <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-3xl p-12 text-center">
          <h2 className="font-barlow text-3xl md:text-4xl font-bold text-white uppercase mb-4">
            Conversatorios Exclusivos
          </h2>
          <p className="text-lg text-zinc-400 mb-6 max-w-2xl mx-auto">
            Accede a todo el contenido de mindset con la membresía Premium.
            Incluye conversatorios en vivo, podcasts y material descargable.
          </p>
          <a href="/programs">
            <Button className="bg-purple-600 hover:bg-purple-500 text-white rounded-full px-12 py-6 text-lg font-bold glow-pink">
              Ver Membresía Premium
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Mindset;
